import React from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { AuthListener } from './components/auth/AuthListener'
import ProtectedRoute from './components/routing/ProtectedRoute'
import AuthLayout from './layouts/AuthLayout'
import DashboardLayout from './layouts/DashboardLayout'
import SignIn from './modules/authentication/pages/SignIn'
import SignUp from './modules/authentication/pages/SignUp'
import ForgotPassword from './modules/authentication/pages/ForgotPassword'
import Dashboard from './modules/dashboard/pages/Dashboard'
import LandingPage from './modules/landing/pages/LandingPage'
import OrganizationsPage from './modules/system/organizations/pages/OrganizationsPage'
import DepartmentsPage from './modules/organization/departments/pages/DepartmentsPage'
import PositionsPage from './modules/organization/positions/pages/PositionsPage'
import WorkplacesPage from './modules/organization/workplaces/pages/WorkplacesPage'
import RolesPage from './modules/organization/roles/pages/RolesPage'
import RoleMenuManagementPage from './modules/organization/roles/pages/RoleMenuManagementPage'
import OrgTreePage from './modules/organization/org-tree/pages/OrgTreePage'
import MyOrganizationPage from './modules/organization/my-organization/pages/MyOrganizationPage'
import OrganizationTypesPage from './modules/system/organization-types/pages/OrganizationTypesPage'
import NaceCodesPage from './modules/system/nace-codes/pages/NaceCodesPage'
import MenusPage from './modules/system/menus/pages/MenusPage'
import TenantsPage from './modules/system/tenants/pages/TenantsPage'
import UsersPage from './modules/system/users/pages/UsersPage'
import EmployeesPage from './modules/hr/employees/pages/EmployeesPage'
import EmployeeNewPage from './modules/hr/employees/pages/EmployeeNewPage'
import FormMasterPage from './modules/system/form-master/pages/FormMasterPage'
import FormMasterFormPage from './modules/system/form-master/pages/FormMasterFormPage'
import IntegrationsPage from './modules/system/integrations/pages/IntegrationsPage'
import IntegrationBuilderPage from './modules/system/integrations/pages/IntegrationBuilderPage'
import ConnectorEditPage from './modules/system/integrations/pages/ConnectorEditPage'
import MappingEditPage from './modules/system/integrations/pages/MappingEditPage'
import EmployeeTypesPage from './modules/organization/option-defs/pages/EmployeeTypesPage'
import LicenseTypesPage from './modules/organization/option-defs/pages/LicenseTypesPage'
import MilitaryStatusPage from './modules/organization/option-defs/pages/MilitaryStatusPage'
import JobDefinitionsPage from './modules/organization/option-defs/pages/JobDefinitionsPage'
import PersonnelStatusesPage from './modules/organization/option-defs/pages/PersonnelStatusesPage'
import PlaceholderPage from './modules/system/PlaceholderPage'

function App() {
  const { i18n } = useTranslation()

  // Set HTML dir and lang attributes based on language
  useEffect(() => {
    document.documentElement.setAttribute('dir', i18n.dir())
    document.documentElement.setAttribute('lang', i18n.language)
  }, [i18n.language, i18n.dir])

  return (
    <>
      <AuthListener />
      <Routes>
        {/* Landing Page */}
      <Route path="/" element={<LandingPage />} />

      {/* Public Routes */}
      <Route element={<AuthLayout />}>
        <Route path="/sign-in" element={<SignIn />} />
        <Route path="/sign-up" element={<SignUp />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
      </Route>

      {/* Protected Routes */}
      <Route
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/dashboard" element={<Dashboard />} />

        {/* Organizasyon (Pearl parity) */}
        <Route path="/organization/my-organization" element={<MyOrganizationPage />} />
        <Route path="/organization/workplaces" element={<WorkplacesPage />} />
        <Route path="/organization/roles" element={<RolesPage />} />
        <Route path="/organization/roles/:roleId/menu-management" element={<RoleMenuManagementPage />} />
        <Route path="/organization/departments" element={<DepartmentsPage />} />
        <Route path="/hr/positions" element={<PositionsPage />} />
        <Route path="/hr/employees" element={<EmployeesPage />} />
        <Route path="/hr/employees/new" element={<EmployeeNewPage />} />
        <Route path="/hr/employees/:employeeId/edit" element={<EmployeeNewPage />} />
        <Route path="/employee/new" element={<EmployeeNewPage />} />
        <Route path="/employee/:employeeId/edit" element={<EmployeeNewPage />} />
        <Route path="/employee/list" element={<Navigate to="/hr/employees" replace />} />
        <Route path="/organization/org-tree" element={<OrgTreePage />} />
        <Route path="/organization/option-defs" element={<Navigate to="/organization/option-defs/organization-types" replace />} />
        <Route path="/organization/option-defs/organization-types" element={<OrganizationTypesPage />} />
        <Route path="/organization/option-defs/employee-types" element={<EmployeeTypesPage />} />
        <Route path="/organization/option-defs/license-types" element={<LicenseTypesPage />} />
        <Route path="/organization/option-defs/military-status" element={<MilitaryStatusPage />} />
        <Route path="/organization/option-defs/job-definitions" element={<JobDefinitionsPage />} />
        <Route path="/organization/option-defs/personnel-statuses" element={<PersonnelStatusesPage />} />

        {/* System (legacy / alternate) */}
        <Route path="/system/organizations" element={<OrganizationsPage />} />
        <Route path="/system/departments" element={<Navigate to="/organization/departments" replace />} />
        <Route path="/system/organization-types" element={<Navigate to="/organization/option-defs/organization-types" replace />} />
        <Route path="/system/data-templates" element={<Navigate to="/organization/option-defs/organization-types" replace />} />
        <Route path="/system/employment-statuses" element={<PlaceholderPage title="İstihdam Durumları" description="İstihdam durumu tanımları" />} />
        <Route path="/system/occupations" element={<PlaceholderPage title="Meslekler" description="Meslek tanımları" />} />
        <Route path="/system/cost-centers" element={<PlaceholderPage title="Maliyet Merkezleri" description="Maliyet merkezi yönetimi" />} />
        <Route path="/system/nace-codes" element={<NaceCodesPage />} />
        <Route path="/system/department-templates" element={<PlaceholderPage title="Departman Şablonları" description="Departman şablon yönetimi" />} />
        <Route path="/system/position-templates" element={<PlaceholderPage title="Pozisyon Şablonları" description="Pozisyon şablon yönetimi" />} />
        <Route path="/system/positions" element={<Navigate to="/hr/positions" replace />} />
        <Route path="/organization/positions" element={<Navigate to="/hr/positions" replace />} />
        <Route path="/system/nace-class-codes" element={<PlaceholderPage title="NACE Sınıf Kodları" description="NACE sınıf kod yönetimi" />} />
        <Route path="/system/form-master" element={<FormMasterPage />} />
        <Route path="/system/form-master/new" element={<FormMasterFormPage />} />
        <Route path="/system/form-master/:id/edit" element={<FormMasterFormPage />} />
        <Route path="/system/integrations" element={<IntegrationsPage />} />
        <Route path="/system/integrations/builder" element={<IntegrationBuilderPage />} />
        <Route path="/system/integrations/connectors/:id/edit" element={<ConnectorEditPage />} />
        <Route path="/system/integrations/mappings/:id/edit" element={<MappingEditPage />} />
        <Route path="/system/builder" element={<Navigate to="/system/integrations/builder" replace />} />
        <Route path="/system/menus" element={<MenusPage />} />
        <Route path="/system/tenants" element={<TenantsPage />} />
        <Route path="/system/users" element={<UsersPage />} />
        <Route path="/system/roles" element={<Navigate to="/organization/roles" replace />} />
      </Route>

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  )
}

export default App

